<template>
  <div v-title="$route.meta.title" class="self-training-exams">
    <header class="exam-header">
      <h2>我的问卷</h2>
      <p>查看并完成分配给你的问卷</p>
    </header>

    <div class="exam-tabs" role="tablist" aria-label="问卷分类">
      <button class="exam-tab" :class="{ active: activeTab === 'pending' }"
              type="button" role="tab" :aria-selected="activeTab === 'pending'"
              @click="changeTab('pending')">
        待完成 <span class="exam-count pending">{{ countsLoaded ? counts.pending : '—' }}</span>
      </button>
      <button class="exam-tab" :class="{ active: activeTab === 'completed' }"
              type="button" role="tab" :aria-selected="activeTab === 'completed'"
              @click="changeTab('completed')">
        已完成 <span class="exam-count completed">{{ countsLoaded ? counts.completed : '—' }}</span>
      </button>
    </div>

    <div v-loading="loading" class="exam-list" role="tabpanel">
      <article v-for="exam in exams" :key="exam.id" class="exam-card">
        <div class="exam-icon"><i class="el-icon-document"></i></div>
        <div class="exam-content">
          <h3>{{ exam.title }}</h3>
          <div class="exam-meta">
            <span><i class="el-icon-question"></i>{{ exam.question_count }} 题</span>
            <span><i class="el-icon-time"></i>考试时限：{{ exam.exam_duration }} 分钟</span>
            <span v-if="exam.created_at"><i class="el-icon-date"></i>创建时间：{{ exam.created_at }}</span>
          </div>
          <div v-if="activeTab === 'completed'" class="exam-result">
            <span v-if="exam.submitted_at">完成时间：{{ exam.submitted_at }}</span>
            <span v-if="exam.open_book_exam === 2 && exam.full_score > 0">
              得分：{{ exam.score }} / {{ exam.full_score }}
            </span>
          </div>
        </div>
        <el-button v-if="activeTab === 'pending'" class="exam-action" type="primary"
                   :loading="startingExamId === exam.id"
                   :disabled="startingExamId !== null || loading" @click="startExam(exam)">
          {{ exam.action === 'continue' ? '继续考试' : '开始考试' }}
        </el-button>
        <div v-else class="exam-result-actions">
          <el-tag :type="exam.record_status === 3 ? 'warning' : 'success'" size="small">
            {{ exam.record_status === 3 ? '未通过' : '已完成' }}
          </el-tag>
          <el-button class="exam-result-action" type="primary" plain size="small"
                     :disabled="!exam.record_id" @click="viewResult(exam)">
            查看详情
          </el-button>
        </div>
      </article>

      <div v-if="!loading && loadFailed" class="exam-empty">
        <p>问卷加载失败</p>
        <el-button class="exam-retry" size="small" plain type="primary" @click="fetchExams">重新加载</el-button>
      </div>
      <div v-else-if="!loading && !exams.length" class="exam-empty">
        <i class="el-icon-document"></i>
        <p>{{ activeTab === 'pending' ? '暂无待完成问卷' : '暂无已完成问卷' }}</p>
      </div>
    </div>

    <el-pagination v-if="!loadFailed && total > pageSize" class="exam-pagination" background
                   layout="prev, pager, next, total" :current-page="currentPage"
                   :page-size="pageSize" :total="total" :disabled="loading"
                   @current-change="changePage"/>
  </div>
</template>

<script>
export default {
  name: 'SelfTrainingExaminationTable',
  data () {
    return {
      activeTab: 'pending',
      counts: { pending: 0, completed: 0 },
      countsLoaded: false,
      exams: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      loading: false,
      loadFailed: false,
      startingExamId: null,
      requestId: 0,
    }
  },
  created () {
    this.fetchExams()
  },
  beforeDestroy () {
    this.requestId++
  },
  methods: {
    fetchExams () {
      const requestId = ++this.requestId
      this.loading = true
      this.loadFailed = false
      this.exams = []
      return request.common('getSelfTrainingExams', {
        state: this.activeTab,
        current_page: this.currentPage,
        per_page: this.pageSize,
      }).then(res => {
        if (requestId !== this.requestId) return
        const data = res && res.data ? res.data : {}
        this.exams = Array.isArray(data.data) ? data.data : []
        this.total = Number(data.total) || 0
        this.currentPage = Number(data.current_page) || this.currentPage
        const counts = data.counts || {}
        this.counts = { pending: Number(counts.pending) || 0, completed: Number(counts.completed) || 0 }
        this.countsLoaded = true
      }).catch(() => {
        if (requestId !== this.requestId) return
        this.loadFailed = true
        this.total = 0
      }).finally(() => {
        if (requestId === this.requestId) this.loading = false
      })
    },
    changeTab (tab) {
      if (tab === this.activeTab) return
      this.activeTab = tab
      this.currentPage = 1
      return this.fetchExams()
    },
    changePage (page) {
      this.currentPage = page
      return this.fetchExams()
    },
    startExam (exam) {
      if (this.startingExamId !== null || this.activeTab !== 'pending' || this.loading) return
      this.startingExamId = exam.id
      const continuing = exam.action === 'continue'
      return request.common('startSelfTrainingExam', { id: exam.id }).then(res => {
        const record = res.data || {}
        const recordId = record.record_id || record.id || exam.record_id
        const attemptId = record.attemptId || record.attempt_id
        this.$set(exam, 'record_id', recordId)
        this.$set(exam, 'record_status', record.status || 1)
        this.$set(exam, 'action', 'continue')
        this.$message.info(continuing ? '已恢复答卷' : '答卷已准备好')
        const query = {}
        if (recordId) query.record_id = recordId
        if (attemptId) query.attemptId = attemptId
        if (this.$router && this.$router.push) {
          this.$router.push({ path: `/training/self-examination/${exam.id}`, query })
        }
        return this.fetchExams()
      }).catch(() => {
        // 请求封装已显示错误，保持原来的开始/继续状态，允许重试。
      }).finally(() => {
        this.startingExamId = null
      })
    },
    viewResult (exam) {
      if (!exam.record_id || !this.$router || !this.$router.push) return
      this.$router.push(`/training/self-examination/${exam.id}/result/${exam.record_id}`)
    },
  },
}
</script>

<style scoped lang="scss">
.self-training-exams {
  min-height: calc(100vh - 160px);
  padding: 26px 30px;
  background: #f5f7fa;
  color: #183153;
}

.exam-header {
  h2 { margin: 0 0 8px; font-size: 20px; font-weight: 600; }
  p { margin: 0; color: #7b8ba1; font-size: 14px; }
}

.exam-tabs { display: flex; gap: 24px; margin: 22px 0 16px; border-bottom: 1px solid #e1e6ed; }
.exam-tab {
  padding: 8px 0 13px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #75849a;
  cursor: pointer;
  font-size: 14px;
  &.active { color: #2165ff; border-bottom-color: #2165ff; }
  &:focus-visible { outline: 2px solid #2165ff; outline-offset: 2px; }
}
.exam-count {
  display: inline-block;
  min-width: 20px;
  margin-left: 5px;
  padding: 0 5px;
  border-radius: 12px;
  line-height: 20px;
  font-size: 12px;
  &.pending { color: #ef8b27; background: #fff0df; }
  &.completed { color: #1aa868; background: #def5e9; }
}
.exam-list { min-height: 180px; }
.exam-card {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 14px;
  padding: 20px;
  border: 1px solid #edf0f5;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(24, 49, 83, .04);
}
.exam-icon {
  display: flex;
  flex: 0 0 44px;
  align-items: center;
  justify-content: center;
  height: 44px;
  border-radius: 8px;
  background: #e8efff;
  color: #2165ff;
  font-size: 24px;
}
.exam-content {
  flex: 1;
  min-width: 0;
  h3 { margin: 0 0 8px; overflow-wrap: break-word; font-size: 14px; font-weight: 500; }
}
.exam-meta, .exam-result {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  color: #7b8ba1;
  font-size: 12px;
  i { margin-right: 4px; }
}
.exam-result { margin-top: 8px; }
.exam-action { flex-shrink: 0; border-radius: 8px; background: #2165ff; border-color: #2165ff; }
.exam-result-actions { display: flex; flex-shrink: 0; align-items: center; gap: 12px; }
.exam-result-action { border-radius: 8px; }
.exam-empty { padding: 60px 20px; text-align: center; color: #909eb1; i { font-size: 32px; } }
.exam-pagination { margin-top: 22px; text-align: center; }

@media (max-width: 768px) {
  .self-training-exams { padding: 20px 16px; }
  .exam-card { flex-wrap: wrap; gap: 12px; padding: 16px; }
  .exam-content { flex-basis: calc(100% - 60px); }
  .exam-action { margin-left: 56px; }
  .exam-result-actions { margin-left: 56px; }
}
</style>



// WEBPACK FOOTER //
// src/views/training/pages/exam/SelfTrainingExaminationTable.vue